/**
 * Pure eviction-ordering logic for the service worker's storage-pressure
 * policy (`src/sw.ts`'s `relieveStoragePressure`).
 *
 * Extracted from `sw.ts` purely so it can be tested: that module registers
 * `self.addEventListener` handlers and Workbox routes at import time, so it
 * cannot be imported into a test at all. Everything here is deliberately
 * dependency-free -- no `@config`, no DOM, no Workbox -- because `sw.ts` is
 * bundled by `vite-plugin-pwa`'s `injectManifest` in a reduced Vite
 * environment where this repo's plugins (and therefore `virtual:pwa-config`)
 * don't resolve.
 */

export interface AssetOwnerRecord {
	issueId: string;
	cacheName: string;
	/** ms since epoch; absent on records written before 2026-08-28. */
	savedAt?: number;
	/**
	 * True for an asset explicitly downloaded for guaranteed offline access
	 * (`entities/downloaded-issue`'s `saveDownloadedIssue`, via the SW's
	 * `PIN_ISSUE_ASSETS` message) -- exempt from storage-pressure eviction
	 * until explicitly un-pinned (`removeDownloadedIssue`'s
	 * `UNPIN_ISSUE_ASSETS`). Absent/false for everything else (passive/
	 * incidental caching, or an un-pinned former download), which stays
	 * eligible exactly as before this field existed.
	 */
	permanent?: boolean;
	/**
	 * Response byte size, captured once at fetch time (`sw.ts`'s
	 * `recordAssetOwner` callers) -- read from `Content-Length` when present,
	 * a `Blob.size` read otherwise. Absent on records written before this
	 * field existed; the per-issue storage calculator
	 * (`entities/downloaded-issue`'s `issue-storage.ts`) reads a missing
	 * value as `0`, same convention as `savedAt` above.
	 */
	size?: number;
}

/**
 * Orders issues oldest-download-first, for eviction under storage pressure.
 *
 * An issue's age is the age of its *oldest* asset, not its newest: assets for
 * one issue arrive over multiple download passes (images eagerly, video and
 * audio lazily), so taking the newest would let a long-lived issue keep
 * refreshing its own timestamp and never become evictable.
 *
 * A missing `savedAt` is read as `0` — oldest — which is both the safe
 * default and factually right, since those records predate the field.
 *
 * `permanent` records are excluded entirely before ranking, not just
 * deprioritized: they're not eviction candidates at all (see
 * `AssetOwnerRecord.permanent`'s own doc comment), so an issue whose only
 * remaining assets are all pinned never enters the queue in the first place
 * -- `sw.ts`'s `evictIssue` would find nothing to delete for it anyway, and
 * skipping it here avoids `relieveStoragePressure` wasting a no-op pass on
 * it before moving to an issue eviction can actually reclaim space from.
 */
export function buildEvictionQueue(
	entries: Array<[string, AssetOwnerRecord]>,
): Array<string> {
	const oldestByIssue = new Map<string, number>();

	for (const [, record] of entries) {
		if (record.permanent) {
			continue;
		}
		const savedAt = record.savedAt ?? 0;
		const current = oldestByIssue.get(record.issueId);
		if (current === undefined || savedAt < current) {
			oldestByIssue.set(record.issueId, savedAt);
		}
	}

	return [...oldestByIssue.entries()]
		.sort((a, b) => a[1] - b[1])
		.map(([issueId]) => issueId);
}
